import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createQuote,
  deleteAttachment,
  deleteQuote,
  editQuote,
  getDuplicateQuoteById,
  getQuoteById,
  getQuotePdfPreview,
  getQuotes,
  sendQuoteEmail,
  updateQuoteStatus,
} from "./thunk";
import {
  ApiResponse,
  PaginatedQuoteSummaryPayload,
  PaginationMeta,
  QuoteSummary,
} from "types/ApiResponse";
import {
  QuotesPayloadData,
  QuoteDetailsPayload,
  StatusPayload,
} from "types/quote.api";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface QuoteStatus {
  fetch: RequestStatus;
  fetchById: RequestStatus;
  add: RequestStatus;
  edit: RequestStatus;
  delete: RequestStatus;
  updateQuoteStatus: RequestStatus;
  sendEmail: RequestStatus;
  pdfPreview: RequestStatus;
}

interface QuoteErrors {
  fetch: string | null;
  fetchById: string | null;
  add: string | null;
  edit: string | null;
  delete: string | null;
  updateQuoteStatus: string | null;
  sendEmail: string | null;
  pdfPreview: string | null;
}

export interface QuoteState {
  quotes: QuotesPayloadData[];
  ids: number[];
  quoteDetails: QuoteDetailsPayload | null;
  pagination: PaginationMeta;
  status: QuoteStatus;
  errors: QuoteErrors;
  summary: QuoteSummary | null;
  pdfPreviewHtml: string | null;
}

export const initialState: QuoteState = {
  quotes: [],
  ids: [],
  quoteDetails: null,
  summary: null,
  pdfPreviewHtml: null,
  pagination: {
    current_page: 0,
    per_page: 0,
    total: 0,
    last_page: 0,
    from: 0,
    to: 0,
    links: [],
    path: "",
  },

  status: {
    fetch: "idle",
    fetchById: "idle",
    add: "idle",
    edit: "idle",
    delete: "idle",
    updateQuoteStatus: "idle",
    sendEmail: "idle",
    pdfPreview: "idle",
  },

  errors: {
    fetch: null,
    fetchById: null,
    add: null,
    edit: null,
    delete: null,
    updateQuoteStatus: null,
    sendEmail: null,
    pdfPreview: null,
  },
};

const quoteSlice = createSlice({
  name: "quote",
  initialState,
  reducers: {
    clearQuoteDetails: (state) => {
      state.quoteDetails = null;
    },
    clearPdfPreview: (state) => {
      state.pdfPreviewHtml = null;
      state.status.pdfPreview = "idle";
      state.errors.pdfPreview = null;
    },
  },
  extraReducers: (builder) => {
    // get Quotes
    builder.addCase(getQuotes.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
    });
    builder.addCase(
      getQuotes.fulfilled,
      (
        state,
        action: PayloadAction<
          ApiResponse<PaginatedQuoteSummaryPayload<QuotesPayloadData>>
        >,
      ) => {
        state.status.fetch = "succeeded";
        const responsePayload = action.payload?.payload;
        state.quotes = responsePayload?.data || [];
        state.ids = (responsePayload?.data || []).map((quote) => quote.id);
        state.pagination = responsePayload?.meta;
        state.summary = responsePayload?.summary || null;
      },
    );
    builder.addCase(getQuotes.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
    });

    // get Quote By Id
    builder.addCase(getQuoteById.pending, (state) => {
      state.status.fetchById = "loading";
      state.errors.fetchById = null;
    });
    builder.addCase(
      getQuoteById.fulfilled,
      (state, action: PayloadAction<ApiResponse<QuoteDetailsPayload>>) => {
        state.status.fetchById = "succeeded";
        const quote = action.payload.payload;
        state.quoteDetails = quote;
      },
    );
    builder.addCase(getQuoteById.rejected, (state, action) => {
      state.status.fetchById = "failed";
      state.errors.fetchById = action.payload || null;
    });

    //Get Duplicate Quote By Id
    builder.addCase(getDuplicateQuoteById.pending, (state) => {
      state.status.fetchById = "loading";
      state.errors.fetchById = null;
    });
    builder.addCase(
      getDuplicateQuoteById.fulfilled,
      (state, action: PayloadAction<ApiResponse<QuoteDetailsPayload>>) => {
        state.status.fetchById = "succeeded";
        // const quote = action.payload.payload;
        // state.quoteDetails = quote;
      },
    );
    builder.addCase(getDuplicateQuoteById.rejected, (state, action) => {
      state.status.fetchById = "failed";
      state.errors.fetchById = action.payload || null;
    });

    // create quote
    builder.addCase(createQuote.pending, (state) => {
      state.status.add = "loading";
      state.errors.add = null;
    });
    builder.addCase(
      createQuote.fulfilled,
      (state, action: PayloadAction<ApiResponse<QuoteDetailsPayload>>) => {
        state.status.add = "succeeded";
        let quoteData = action.payload.payload;
        state.quoteDetails = quoteData;
        state.pagination.to++;
        state.pagination.total++;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page,
        );
      },
    );
    builder.addCase(createQuote.rejected, (state, action) => {
      state.status.add = "failed";
      state.errors.add = action.payload || null;
    });

    // delete Quote
    builder.addCase(deleteQuote.pending, (state) => {
      state.status.delete = "loading";
      state.errors.delete = null;
    });
    builder.addCase(
      deleteQuote.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        state.status.delete = "succeeded";
        const deletedQuoteId = action.payload.payload;
        state.quotes = state.quotes.filter(
          (quote) => quote.id !== deletedQuoteId,
        );
        state.ids = state.ids.filter((id) => id !== deletedQuoteId);
        state.pagination.to--;
        state.pagination.total--;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page,
        );
      },
    );
    builder.addCase(deleteQuote.rejected, (state, action) => {
      state.status.delete = "failed";
      state.errors.delete = action.payload || null;
    });

    //Update Quote Status
    builder.addCase(updateQuoteStatus.pending, (state) => {
      state.status.updateQuoteStatus = "loading";
      state.errors.updateQuoteStatus = null;
    });
    builder.addCase(
      updateQuoteStatus.fulfilled,
      (state, action: PayloadAction<ApiResponse<StatusPayload>>) => {
        state.status.updateQuoteStatus = "succeeded";
        const statusPayload = action.payload.payload;

        state.quotes = state.quotes.map((e) => {
          if (e.id === statusPayload.quote_id) {
            return { ...e, status: statusPayload.status };
          } else {
            return e;
          }
        });
        if (
          state.quoteDetails &&
          state.quoteDetails.id === statusPayload.quote_id
        ) {
          state.quoteDetails.status = statusPayload.status;

          if (statusPayload.status === "approved") {
            state.quoteDetails.is_editable = false;
          }
        }
      },
    );
    builder.addCase(updateQuoteStatus.rejected, (state, action) => {
      state.status.updateQuoteStatus = "failed";
      state.errors.updateQuoteStatus = action.payload || null;
    });

    //Edit Quote
    builder.addCase(editQuote.pending, (state) => {
      state.status.edit = "loading";
      state.errors.edit = null;
    });
    builder.addCase(editQuote.fulfilled, (state, action) => {
      state.status.edit = "succeeded";
      const updatedQuoteDetails = action.payload.payload;
      console.log("Updated quote Detail", updatedQuoteDetails);

      state.quoteDetails = updatedQuoteDetails;
    });
    builder.addCase(editQuote.rejected, (state, action) => {
      state.status.edit = "failed";
      state.errors.edit = action.payload || null;
    });

    // delete attachment by quote id
    builder.addCase(
      deleteAttachment.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        if (state.quoteDetails?.attachments?.length) {
          const deletedId = action.payload.payload;

          state.quoteDetails.attachments =
            state.quoteDetails.attachments.filter(
              (attachment) => attachment.id !== deletedId,
            );
        }
      },
    );

    //send Quote Email
    builder.addCase(sendQuoteEmail.pending, (state) => {
      state.status.sendEmail = "loading";
      state.errors.sendEmail = null;
    });
    builder.addCase(
      sendQuoteEmail.fulfilled,
      (state, action: PayloadAction<ApiResponse<null>>) => {
        state.status.sendEmail = "succeeded";
        state.errors.sendEmail = null;
        if (state.quoteDetails?.status?.toLowerCase() === "completed") {
          state.quoteDetails.status = "sent";
        }
      },
    );
    builder.addCase(sendQuoteEmail.rejected, (state, action) => {
      state.status.sendEmail = "failed";
      state.errors.sendEmail = action.payload || null;
    });

    // get Quote Pdf Preview
    builder.addCase(getQuotePdfPreview.pending, (state) => {
      state.status.pdfPreview = "loading";
      state.errors.pdfPreview = null;
    });
    builder.addCase(
      getQuotePdfPreview.fulfilled,
      (state, action: PayloadAction<string>) => {
        state.status.pdfPreview = "succeeded";
        state.pdfPreviewHtml = action.payload;
      },
    );
    builder.addCase(getQuotePdfPreview.rejected, (state, action) => {
      state.status.pdfPreview = "failed";
      state.errors.pdfPreview = action.payload || null;
    });
  },
});

export default quoteSlice.reducer;

export const { clearQuoteDetails, clearPdfPreview } = quoteSlice.actions;

export const selectQuotesState = (state: any) =>
  state.quote as typeof initialState;
